import { useEffect, useRef } from "react";

/** Diálogos abiertos (Modal y Drawer), del más viejo al más nuevo. */
const pila: string[] = [];
/** `overflow` del body antes de abrir el primer diálogo de la pila. */
let overflowOriginal: string | null = null;

/**
 * Esc cierra el diálogo y, mientras haya alguno abierto, el body no hace scroll.
 *
 * Sólo el diálogo de arriba atiende Esc: con una confirmación encima de un
 * formulario, Esc cerraba los dos y se perdía lo escrito. `onClose` va por
 * ref porque muchos callers lo pasan como arrow inline: si el efecto
 * dependiera de él, cada render re-apilaría el diálogo de abajo encima del
 * que se abrió después.
 *
 * El bloqueo de scroll es de la pila, no de cada diálogo: se guarda el
 * `overflow` al abrir el primero y se devuelve al cerrar el último. Antes
 * cada uno restauraba "su" valor previo, y si dos se cerraban en el mismo
 * render ("Descartar cambios" cierra la confirmación y el formulario) React
 * desmonta primero el de afuera: el de adentro dejaba el body en `hidden` y
 * la app quedaba sin scroll hasta recargar.
 */
export function useEscapeDelDialogo(open: boolean, id: string, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    if (pila.length === 0) {
      overflowOriginal = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    pila.push(id);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && pila[pila.length - 1] === id) {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = pila.lastIndexOf(id);
      if (i >= 0) pila.splice(i, 1);
      if (pila.length === 0) {
        document.body.style.overflow = overflowOriginal ?? "";
        overflowOriginal = null;
      }
    };
  }, [open, id]);
}
